/**
 * Клиент WHEP: живой просмотр через WebRTC.
 *
 * Своя реализация вместо `reader.js` от MediaMTX — по трём причинам.
 *
 * Первая: `reader.js` перед началом создаёт три одноразовых RTCPeerConnection и
 * правит SDP руками, чтобы включить стерео PCMU/PCMA, многоканальный Opus и
 * L16. Нам это не нужно ни в одном виде: источник — H.264, звука нет.
 *
 * Вторая: `OPTIONS` мы пропускаем. Его единственная работа — забрать заголовки
 * `Link: rel="ice-server"`, а `webrtcICEServers2` у нас пуст намеренно (сервер
 * знает свой публичный адрес, и STUN ему не нужен). Пропуск убирает и лишний
 * круг, и лишний вызов вебхука авторизации.
 *
 * Третья, главная: политика повторов и наблюдение за состоянием должны быть
 * нашими, а `reader.js` их не отдаёт.
 */

/** Разобранный offer: то, из чего собирается фрагмент для trickle-ICE. */
interface OfferShape {
  iceUfrag: string;
  icePwd: string;
  /** По порядку m-строк: тип медиа и mid. */
  medias: { kind: string; mid: string }[];
}

/**
 * Вытащить из offer то, что нужно фрагменту.
 *
 * Разбор именно offer'а, а не ответа: `a=ice-ufrag` и `a=ice-pwd` в фрагменте
 * обязаны быть НАШИ, иначе сервер не сопоставит кандидатов с сессией. На этом
 * спотыкаются: фрагмент принимается с 204 и тихо игнорируется.
 */
export function parseOffer(sdp: string): OfferShape {
  let iceUfrag = "";
  let icePwd = "";
  const medias: { kind: string; mid: string }[] = [];
  let pending: string | null = null;

  for (const raw of sdp.split("\n")) {
    const line = raw.trim();
    if (line.startsWith("m=")) {
      if (pending !== null) medias.push({ kind: pending, mid: "" });
      pending = line.slice(2).split(" ")[0] ?? "";
    } else if (line.startsWith("a=mid:")) {
      const mid = line.slice("a=mid:".length);
      if (pending !== null) {
        medias.push({ kind: pending, mid });
        pending = null;
      }
    } else if (line.startsWith("a=ice-ufrag:") && iceUfrag === "") {
      iceUfrag = line.slice("a=ice-ufrag:".length);
    } else if (line.startsWith("a=ice-pwd:") && icePwd === "") {
      icePwd = line.slice("a=ice-pwd:".length);
    }
  }
  if (pending !== null) medias.push({ kind: pending, mid: "" });

  return { iceUfrag, icePwd, medias };
}

/**
 * Собрать `application/trickle-ice-sdpfrag`.
 *
 * Обрамление (`a=ice-ufrag`, `a=ice-pwd`, затем `m=` и `a=mid:` на каждую
 * медиа-секцию) выглядит формальностью, но именно оно связывает кандидатов с
 * сессией и с нужной секцией. Ошибка здесь не даёт ошибки: сервер отвечает
 * 204 и молча выбрасывает кандидатов, а соединение потом работает или нет в
 * зависимости от того, хватило ли кандидатов из первоначального offer'а.
 */
export function sdpFragment(
  offer: OfferShape,
  candidates: RTCIceCandidate[],
): string {
  const byIndex = new Map<number, RTCIceCandidate[]>();
  for (const candidate of candidates) {
    const index = candidate.sdpMLineIndex ?? 0;
    const bucket = byIndex.get(index);
    if (bucket) bucket.push(candidate);
    else byIndex.set(index, [candidate]);
  }

  let out = `a=ice-ufrag:${offer.iceUfrag}\r\na=ice-pwd:${offer.icePwd}\r\n`;
  offer.medias.forEach((media, index) => {
    const bucket = byIndex.get(index);
    if (!bucket || bucket.length === 0) return;
    out += `m=${media.kind} 9 RTP/AVP 0\r\na=mid:${media.mid}\r\n`;
    for (const candidate of bucket) {
      out += `a=${candidate.candidate}\r\n`;
    }
  });
  return out;
}

/**
 * Привести относительный `Location` от сервера к абсолютному адресу.
 *
 * Сервер уже переписывает его под свой префикс, но резолвить всё равно надо
 * относительно текущей страницы, а не относительно адреса запроса.
 */
export function resolveSession(location: string): string {
  return new URL(location, window.location.href).toString();
}

export class WhepError extends Error {
  constructor(
    message: string,
    readonly status: number | null,
  ) {
    super(message);
    this.name = "WhepError";
  }
}

/** Установленное соединение и то, чем его закрыть. */
export interface WhepSession {
  pc: RTCPeerConnection;
  close: () => Promise<void>;
}

/**
 * Поднять просмотр камеры.
 *
 * `iceServers: []` — сознательно: сервер публичен, и рефлексивный адрес
 * браузеру не нужен, чтобы отправить на него пакет.
 */
export async function connect(
  camera: string,
  onTrack: (stream: MediaStream) => void,
): Promise<WhepSession> {
  const pc = new RTCPeerConnection({ iceServers: [] });
  pc.addTransceiver("video", { direction: "recvonly" });
  pc.addTransceiver("audio", { direction: "recvonly" });

  pc.addEventListener("track", (event) => {
    if (event.streams[0]) onTrack(event.streams[0]);
  });

  const offer = await pc.createOffer();
  if (!offer.sdp) throw new WhepError("браузер не собрал offer", null);
  await pc.setLocalDescription(offer);
  const shape = parseOffer(offer.sdp);

  const res = await fetch(`/api/stream/${encodeURIComponent(camera)}/whep`, {
    method: "POST",
    headers: { "Content-Type": "application/sdp" },
    body: offer.sdp,
    cache: "no-store",
  });

  if (res.status !== 201) {
    pc.close();
    // Коды разведены по смыслу: 401 лечится входом, 403 — нет, 404 значит
    // «камера есть, но никто не публикует», 503 — упёрлись в лимит зрителей.
    throw new WhepError(`WHEP ответил ${res.status}`, res.status);
  }

  const location = res.headers.get("location");
  if (location === null) {
    pc.close();
    throw new WhepError("в ответе WHEP нет Location", 201);
  }
  const sessionUrl = resolveSession(location);

  await pc.setRemoteDescription({ type: "answer", sdp: await res.text() });

  // Кандидаты отправляем пачками: по одному PATCH на каждого — это десяток
  // запросов на установление одной сессии, и каждый стоит вызова вебхука
  // авторизации на сервере.
  let queue: RTCIceCandidate[] = [];
  let flushing: number | null = null;
  const flush = async () => {
    flushing = null;
    const batch = queue;
    queue = [];
    if (batch.length === 0) return;
    try {
      await fetch(sessionUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/trickle-ice-sdpfrag",
          "If-Match": "*",
        },
        body: sdpFragment(shape, batch),
        cache: "no-store",
      });
    } catch (err) {
      console.warn("не отправить кандидатов ICE", err);
    }
  };

  pc.addEventListener("icecandidate", (event) => {
    if (event.candidate === null) return;
    queue.push(event.candidate);
    if (flushing === null) flushing = window.setTimeout(flush, 50);
  });

  const close = async () => {
    if (flushing !== null) window.clearTimeout(flushing);
    pc.close();
    try {
      // keepalive: закрытие вкладки не должно оставлять сессию висеть на
      // сервере до таймаута.
      await fetch(sessionUrl, { method: "DELETE", keepalive: true });
    } catch {
      // Сервер всё равно уберёт её по возрасту.
    }
  };

  return { pc, close };
}
